import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const PHASE_LABEL = {
  skin_resilience: "Skin Resilience",
  making_actives_count: "Making Actives Count",
};

/**
 * Modal that lets the user pick a slot in their protocol to swap a product into.
 * Only shows the user's CURRENT phase (Skin Resilience swaps auto-carry to MAC
 * for the four shared categories — that logic lives in the backend).
 * Props:
 *   product: { id, product_name, category }
 *   onClose(): void
 *   onSwapped(slotKey): void
 */
export default function SwapPicker({ product, onClose, onSwapped }) {
  const { user } = useAuth();
  const currentPhase = user?.current_phase === "making_actives_count" ? "making_actives_count" : "skin_resilience";
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [popular, setPopular] = useState({}); // slot_key -> [{product_name,count}]

  useEffect(() => {
    api.get("/protocols/all?original=true").then(async (r) => {
      const d = r.data[currentPhase];
      setData(d);
      if (!d) return;
      const calls = [];
      for (const time of ["am", "pm"]) {
        for (let i = 0; i < d[time].length; i++) {
          const slotKey = `${currentPhase}:${time}:${i}`;
          calls.push(
            api
              .get(`/swap-popularity?slot_key=${encodeURIComponent(slotKey)}&limit=3`)
              .then((res) => [slotKey, res.data?.items || []])
              .catch(() => [slotKey, []])
          );
        }
      }
      const results = await Promise.all(calls);
      const map = {};
      for (const [k, v] of results) map[k] = v;
      setPopular(map);
    });
  }, [currentPhase]);

  const submit = async (time, idx) => {
    setSaving(true);
    setErr("");
    try {
      const slotKey = `${currentPhase}:${time}:${idx}`;
      await api.put("/swaps", {
        slot_key: slotKey,
        product_id: product.id || "",
        product_name: product.product_name,
        product_url: "",
      });
      onSwapped?.(slotKey);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't save swap.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end md:items-center justify-center p-0 md:p-6" data-testid="swap-picker-overlay">
      <div className="bg-[var(--aa-bg)] w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-t-2xl md:rounded-2xl border border-[var(--aa-elevated)] shadow-xl">
        <div className="sticky top-0 bg-[var(--aa-bg)] border-b border-[var(--aa-elevated)] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="aa-eyebrow">Swap into my protocol</div>
            <div className="font-display text-xl mt-1">{product.product_name}</div>
          </div>
          <button onClick={onClose} data-testid="swap-picker-close" className="aa-pill px-3 py-1 hover:bg-[var(--aa-elevated)]">Close</button>
        </div>

        <div className="px-6 py-5">
          <p className="text-sm text-[var(--aa-text-2)] mb-4 leading-relaxed">
            Pick which step in your <strong>{PHASE_LABEL[currentPhase]}</strong> protocol this should replace.
            Your original Dr Rey product is remembered so you can revert any time.
            {currentPhase === "skin_resilience" && " For shared steps (cleanser, hydrating serum, moisturizer, sunscreen), your swap will also carry into Making Actives Count when you advance."}
          </p>

          {!data ? (
            <div className="aa-eyebrow">Loading slots…</div>
          ) : (
            <div>
              <div className="aa-eyebrow mb-3">{PHASE_LABEL[currentPhase]}</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {["am", "pm"].map((time) => (
                  <div key={time} className="aa-card p-4">
                    <div className="aa-eyebrow mb-2">{time === "am" ? "Morning" : "Evening"}</div>
                    <ul className="space-y-2">
                      {data[time].map((s, i) => {
                        const slotKey = `${currentPhase}:${time}:${i}`;
                        const others = popular[slotKey] || [];
                        return (
                          <li key={i}>
                            <button
                              disabled={saving}
                              onClick={() => submit(time, i)}
                              data-testid={`swap-target-${currentPhase}-${time}-${i}`}
                              className="w-full text-left p-3 -mx-1 rounded-md hover:bg-[var(--aa-bg-2)] disabled:opacity-60"
                            >
                              <div className="text-xs uppercase tracking-wider text-[var(--aa-text-3)]">{s.step}</div>
                              <div className="text-sm text-[var(--aa-text)] mt-0.5">{s.product}</div>
                            </button>
                            {others.length > 0 && (
                              <div
                                className="mt-1 ml-2 text-xs text-[var(--aa-text-3)] leading-relaxed"
                                data-testid={`swap-popular-${slotKey}`}
                              >
                                <span className="aa-eyebrow text-[10px]">Others have swapped:</span>
                                <ul className="mt-0.5 ml-1 space-y-0.5">
                                  {others.map((p, k) => (
                                    <li key={k}>· {p.product_name}<span className="opacity-60"> ({p.count})</span></li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {err && <div className="mt-4 text-sm text-[var(--aa-terra)]" data-testid="swap-picker-error">{err}</div>}
        </div>
      </div>
    </div>
  );
}

